import { Briefcase, ExternalLink, Megaphone, MegaphoneOff, Send, Users } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { PermissionGate } from '../../../../auth/PermissionGate';
import { Breadcrumb } from '../../../../components/Breadcrumb';
import { Button } from '../../../../components/Button';
import { ConfirmDialog } from '../../../../components/ConfirmDialog';
import { CountBadge } from '../../../../components/CountBadge';
import { EmptyState } from '../../../../components/EmptyState';
import { ErrorState } from '../../../../components/ErrorState';
import type { FolderTab } from '../../../../components/FolderTabs';
import { HistoryLogsModal } from '../../../../components/index-page/HistoryLogsModal';
import { PreviewTable, type PreviewRow } from '../../../../components/PreviewTable';
import { ProfileBanner } from '../../../../components/profile/ProfileBanner';
import { ProfileCard } from '../../../../components/profile/ProfileCard';
import { ProfileHeader } from '../../../../components/profile/ProfileHeader';
import { ProfileHeaderShell, ProfileHeaderSkeleton } from '../../../../components/profile/ProfileHeaderShell';
import { StatusText } from '../../../../components/table/StatusText';
import { useToast } from '../../../../components/toastContext';
import { ApiError } from '../../../../lib/apiError';
import { formatDateOnly, formatDateTime } from '../../../../lib/formatDate';
import { formatMoney } from '../../../../lib/formatMoney';
import { ApplicantsPanel } from '../../applicants/components/ApplicantsPanel';
import { JobPostingModal } from '../components/JobPostingModal';
import { TagForJobPostingModal } from '../components/TagForJobPostingModal';
import { vacanciesApi } from '../vacanciesApi';
import type { VacancyCycle, VacancyListItem } from '../vacancyTypes';

type TabKey = 'info' | 'applicants';

/**
 * One vacant plantilla item, on the **header** layout — a vacancy reads as one thing: a post in a
 * unit at a grade, and whether it is to be advertised.
 *
 * **The banner owns the tag.** Tagging opens the tag form (a form — its own tick is the gate);
 * removing the tag confirms. The Post card is the PLANTILLA's record, so it carries no Edit and
 * links out instead.
 *
 * **The recruitment CYCLE** (product call, 19 Oct 2026), three stages, read off the item's most
 * recent posting and drawn on Info in the cycle's order:
 *
 * 1. **Job Posting** — Posted by, Posted at, Remarks. Its own card and Edit (`vacancies.update`).
 * 2. **Initial Screening** — recorded per APPLICANT; this card is the DERIVED count, so it has no
 *    Edit (the screening's Edit is on each applicant), and links to the Applicants tab instead.
 * 3. **Initial Interview** — likewise per applicant; likewise a count.
 *
 * **The Applicants tab** is the posting's applicant list, where an applicant is added and screened
 * and their interview scheduled — the same panel the Applicants module's posting profile draws.
 */
export function VacancyProfilePage() {
  const { id = '' } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const toast = useToast();

  const [vacancy, setVacancy] = useState<VacancyListItem | null>(null);
  const [loadError, setLoadError] = useState<ApiError | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [tab, setTab] = useState<TabKey>('info');
  const [historyOpen, setHistoryOpen] = useState(false);
  const [tagging, setTagging] = useState(false);
  const [editingPosting, setEditingPosting] = useState(false);

  /** Removing the tag and publishing WRITE, so they confirm; tagging opens a form, which carries its own tick. */
  const [untagging, setUntagging] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [running, setRunning] = useState(false);
  const [actionError, setActionError] = useState<ApiError | null>(null);

  const reload = useCallback(() => setReloadKey((k) => k + 1), []);

  useEffect(() => {
    const controller = new AbortController();

    vacanciesApi
      .get(id, controller.signal)
      .then((loaded) => {
        setVacancy(loaded);
        setLoadError(null);
      })
      .catch((caught: unknown) => {
        if (caught instanceof DOMException && caught.name === 'AbortError') return;
        setLoadError(caught instanceof ApiError ? caught : new ApiError('The vacancy could not be loaded.', 0));
      });

    return () => controller.abort();
  }, [id, reloadKey]);

  async function confirmUntag() {
    if (!vacancy) return;

    setRunning(true);
    setActionError(null);

    try {
      await vacanciesApi.untag(vacancy.id);
      toast.success(`Removed the job posting tag from ${vacancy.itemNumber}.`);
      setUntagging(false);
      reload();
    } catch (caught) {
      setActionError(caught instanceof ApiError ? caught : new ApiError('The action could not be completed.', 0));
    } finally {
      setRunning(false);
    }
  }

  async function confirmPublish() {
    if (!vacancy) return;

    setRunning(true);
    setActionError(null);

    try {
      await vacanciesApi.publish(vacancy.id);
      toast.success(`Published the job posting for ${vacancy.itemNumber}.`);
      setPublishing(false);
      reload();
    } catch (caught) {
      setActionError(caught instanceof ApiError ? caught : new ApiError('The posting could not be published.', 0));
    } finally {
      setRunning(false);
    }
  }

  const cycle = vacancy?.cycle ?? null;

  const tabs: FolderTab<TabKey>[] = [
    { key: 'info', label: 'Info' },
    {
      // The applicant pool is the Applicants module's — its read key, on the tab AND the panel's fetch.
      key: 'applicants',
      label: (
        <>
          Applicants
          <CountBadge count={cycle?.applicantCount ?? 0} tone="slate" />
        </>
      ),
      permission: 'applicants.read',
    },
  ];

  return (
    <div className="flex h-full min-h-0 min-w-0 flex-col bg-white">
      <Breadcrumb
        segments={[
          'Human Resources',
          'Recruitment',
          { label: 'Vacancies', to: '/vacancies' },
          vacancy ? vacancy.itemNumber : '',
        ]}
      />

      {loadError && !vacancy ? (
        <ErrorState
          message={loadError.message}
          correlationId={loadError.correlationId}
          onRetry={loadError.isNotFound ? undefined : reload}
        />
      ) : !vacancy ? (
        <ProfileHeaderSkeleton tabs={tabs.length} />
      ) : (
        <ProfileHeaderShell<TabKey>
          id="vacancy-profile"
          tabs={tabs}
          activeTab={tab}
          onTabChange={setTab}
          panel={tab === 'applicants' ? 'bleed' : 'cards'}
          header={
            <ProfileHeader
              id="vacancy-profile-header"
              icon={<Briefcase className="h-5 w-5" />}
              title={vacancy.itemNumber}
              // The fact line is mono, for codes; the post and unit are NAMES, so they step out of
              // it the way the personnel header's post does.
              meta={[
                vacancy.postCode,
                <span key="post" className="font-sans">
                  {vacancy.positionTitle}
                </span>,
                <span key="unit" className="font-sans">
                  {vacancy.orgUnitName}
                </span>,
              ]}
              status={statusPill(vacancy)}
              createdBy={vacancy.createdByName}
              createdAt={vacancy.createdAt}
              settings={{ onHistory: () => setHistoryOpen(true) }}
            />
          }
          banner={
            <VacancyBanner
              vacancy={vacancy}
              cycle={cycle}
              onTag={() => setTagging(true)}
              onPublish={() => setPublishing(true)}
              onUntag={() => setUntagging(true)}
            />
          }
        >
          {tab === 'info' ? (
            <>
              {/* 1 — JOB POSTING. The live posting's stage is corrected here; an ended one is history. */}
              <ProfileCard
                id="vacancy-profile-job-posting"
                title="Job Posting"
                onEdit={cycle ? () => setEditingPosting(true) : undefined}
                editPermission="vacancies.update"
                editDisabledReason={
                  cycle && !cycle.isLive ? 'This posting has ended. Its job posting details are kept as they were.' : null
                }
                empty={
                  cycle
                    ? undefined
                    : {
                        title: 'Not posted yet.',
                        description: 'Tagging the vacancy for job posting records who posted it, when, and any remarks.',
                      }
                }
              >
                <PreviewTable variant="table-display" rows={postingRows(cycle)} />
              </ProfileCard>

              {/*
                2 — PUBLISHED (22 Oct 2026). Moved by an ACTION (the banner's Publish), never typed, so the
                card carries no Edit; it states who published and when.
              */}
              <ProfileCard
                id="vacancy-profile-published"
                title="Published"
                empty={
                  cycle?.isPublished
                    ? undefined
                    : {
                        title: 'Not published yet.',
                        description: cycle
                          ? 'Until it is published the posting takes no applicants and is not in Deliberate.'
                          : 'A vacancy is published after it is tagged for job posting.',
                      }
                }
              >
                <PreviewTable variant="table-display" rows={publishedRows(cycle)} />
              </ProfileCard>

              {/* 3 — INITIAL SCREENING. Recorded per applicant; this is the count. */}
              <ProfileCard
                id="vacancy-profile-screening"
                title="Initial Screening"
                menuItems={cycle ? [openApplicantsItem('screening', () => setTab('applicants'))] : undefined}
                empty={
                  cycle && cycle.applicantCount > 0
                    ? undefined
                    : {
                        title: 'No applicant to screen.',
                        description: cycle
                          ? 'Add applicants on the Applicants tab; each is screened from there.'
                          : 'Applicants are recorded once the vacancy is tagged for job posting.',
                      }
                }
              >
                <PreviewTable variant="table-display" rows={screeningRows(cycle)} />
              </ProfileCard>

              {/* 4 — INITIAL INTERVIEW. Scheduled per qualified applicant; this is the count. */}
              <ProfileCard
                id="vacancy-profile-interview"
                title="Initial Interview"
                menuItems={cycle ? [openApplicantsItem('interview', () => setTab('applicants'))] : undefined}
                empty={
                  cycle && cycle.qualifiedCount > 0
                    ? undefined
                    : {
                        title: 'No qualified applicant yet.',
                        description: 'An interview is scheduled for each applicant screened Qualified.',
                      }
                }
              >
                <PreviewTable variant="table-display" rows={interviewRows(cycle)} />
              </ProfileCard>

              <ProfileCard
                id="vacancy-profile-post"
                title="Post"
                // The plantilla's record: no Edit of its fields here, a link out to where they live.
                menuItems={[
                  {
                    key: 'open-post',
                    label: 'Open in Plantilla',
                    icon: <ExternalLink className="h-4 w-4" aria-hidden="true" />,
                    permission: 'plantilla.read',
                    onClick: () => navigate(`/plantilla/${vacancy.plantillaPositionId}`),
                  },
                ]}
              >
                <PreviewTable variant="table-display" rows={postRows(vacancy)} />
              </ProfileCard>

              {/* Never editable: a record of what happened. */}
              <ProfileCard id="vacancy-profile-audit" title="Audit">
                <PreviewTable variant="table-display" rows={auditRows(vacancy)} />
              </ProfileCard>
            </>
          ) : cycle ? (
            <ApplicantsPanel
              posting={{
                id: cycle.jobPostingTagId,
                isActive: cycle.isLive,
                isPublished: cycle.isPublished,
                isCompleted: cycle.isLive && vacancy.isCompleted,
              }}
              onChanged={reload}
            />
          ) : (
            <div className="flex h-full min-w-0 flex-col p-4">
              <EmptyState
                message="This vacancy has never been tagged for job posting, so it has no applicants. Tag it for job posting to start recording them."
                action={{ label: 'Tag for job posting', onClick: () => setTagging(true), permission: 'vacancies.create' }}
              />
            </div>
          )}
        </ProfileHeaderShell>
      )}

      {vacancy ? (
        <HistoryLogsModal
          open={historyOpen}
          onClose={() => setHistoryOpen(false)}
          module="vacancies"
          // A RECORD's history is the id alone — every tag route names the item, so each write
          // carries it as its parent.
          entityId={vacancy.id}
        />
      ) : null}

      {vacancy && tagging ? (
        <TagForJobPostingModal
          open
          initialItemId={vacancy.id}
          onClose={() => setTagging(false)}
          onSaved={() => {
            setTagging(false);
            reload();
          }}
        />
      ) : null}

      {vacancy && cycle && editingPosting ? (
        <JobPostingModal
          itemId={vacancy.id}
          cycle={cycle}
          onClose={() => setEditingPosting(false)}
          onSaved={() => {
            setEditingPosting(false);
            reload();
          }}
        />
      ) : null}

      {vacancy && publishing ? (
        <ConfirmDialog
          open
          onClose={() => {
            setPublishing(false);
            setActionError(null);
          }}
          onConfirm={() => void confirmPublish()}
          title="Publish job posting"
          requireConfirm
          loading={running}
          id="vacancy-publish-dialog"
          body={
            <div className="flex flex-col gap-2">
              <p>
                {`${vacancy.itemNumber} moves to Published: from now on it takes applicants and appears in Deliberate. Publishing cannot be undone; removing the tag still ends the posting.`}
              </p>

              {actionError ? (
                <p role="alert" className="rounded-lg border border-red-200 bg-red-50 p-2 text-xs text-red-700">
                  {actionError.message}
                </p>
              ) : null}
            </div>
          }
        />
      ) : null}

      {vacancy && untagging ? (
        <ConfirmDialog
          open
          onClose={() => {
            setUntagging(false);
            setActionError(null);
          }}
          onConfirm={() => void confirmUntag()}
          title="Remove job posting tag"
          requireConfirm
          loading={running}
          id="vacancy-untag-dialog"
          body={
            <div className="flex flex-col gap-2">
              <p>
                {`${vacancy.itemNumber} stays vacant and is listed under Not tagged. The posting ends: it takes no new applicants, and the ${cycle?.applicantCount ?? 0} already recorded are kept. It can be tagged again at any time, as a new posting.`}
              </p>

              {actionError ? (
                <p role="alert" className="rounded-lg border border-red-200 bg-red-50 p-2 text-xs text-red-700">
                  {actionError.message}
                </p>
              ) : null}
            </div>
          }
        />
      ) : null}
    </div>
  );
}

/** The header pill — the same two words the list uses, or why the item is not a vacancy at all. */
function statusPill(vacancy: VacancyListItem) {
  if (!vacancy.isActive) return <StatusText tone="inactive">Deleted</StatusText>;
  if (!vacancy.isVacant) return <StatusText tone="inactive">Filled</StatusText>;

  if (vacancy.isForJobPosting && vacancy.isCompleted) return <StatusText tone="inactive">Completed</StatusText>;

  if (vacancy.isPublished) return <StatusText tone="active">Published</StatusText>;

  return vacancy.isForJobPosting ? (
    <StatusText tone="active">For job posting</StatusText>
  ) : (
    <StatusText tone="inactive">Not tagged</StatusText>
  );
}

/**
 * Renders for every state, and owns the lifecycle — tag, then PUBLISH, and untag. A tagged posting
 * not yet published offers Publish beside Remove tag; it is the next step and the reason it takes
 * no applicants yet.
 */
function VacancyBanner({
  vacancy,
  cycle,
  onTag,
  onPublish,
  onUntag,
}: {
  vacancy: VacancyListItem;
  cycle: VacancyCycle | null;
  onTag: () => void;
  onPublish: () => void;
  onUntag: () => void;
}) {
  if (!vacancy.isActive) {
    return (
      <ProfileBanner
        id="vacancy-profile-banner"
        tone="slate"
        message="This item is deleted from the plantilla, so it is not a vacancy."
      />
    );
  }

  if (!vacancy.isVacant) {
    return (
      <ProfileBanner
        id="vacancy-profile-banner"
        tone="slate"
        message="This item is filled, so it is no longer a vacancy. A job posting tag ends when somebody is appointed to the item."
      />
    );
  }

  const removeTag = (
    <PermissionGate permission="vacancies.delete">
      <Button
        id="vacancy-profile-untag"
        variant="secondary"
        leading={<MegaphoneOff className="h-4 w-4" aria-hidden="true" />}
        onClick={onUntag}
      >
        Remove tag
      </Button>
    </PermissionGate>
  );

  if (vacancy.isForJobPosting && !vacancy.isPublished) {
    return (
      <ProfileBanner
        id="vacancy-profile-banner"
        tone="blue"
        message={
          <>
            Tagged for job posting by {vacancy.taggedByName ?? 'an unknown user'}
            {vacancy.taggedAt != null ? (
              <>
                {' on '}
                <span className="font-mono">{formatDateTime(vacancy.taggedAt)}</span>
              </>
            ) : null}
            . Not published yet — it takes no applicants and is not in Deliberate until it is.
          </>
        }
        action={
          <div className="flex items-center gap-2">
            <PermissionGate permission="vacancies.publish">
              <Button
                id="vacancy-profile-publish"
                variant="secondary"
                leading={<Send className="h-4 w-4" aria-hidden="true" />}
                onClick={onPublish}
              >
                Publish
              </Button>
            </PermissionGate>
            {removeTag}
          </div>
        }
      />
    );
  }

  if (vacancy.isForJobPosting) {
    return (
      <ProfileBanner
        id="vacancy-profile-banner"
        tone="emerald"
        message={
          <>
            Published
            {cycle?.publishedByName != null ? ` by ${cycle.publishedByName}` : ''}
            {cycle?.publishedAt != null ? (
              <>
                {' on '}
                <span className="font-mono">{formatDateTime(cycle.publishedAt)}</span>
              </>
            ) : null}
            . It takes applicants until the tag is removed or somebody is appointed.
          </>
        }
        action={removeTag}
      />
    );
  }

  return (
    <ProfileBanner
      id="vacancy-profile-banner"
      tone="blue"
      message="This item is vacant and not tagged for job posting."
      action={
        <PermissionGate permission="vacancies.create">
          <Button
            id="vacancy-profile-tag"
            variant="secondary"
            leading={<Megaphone className="h-4 w-4" aria-hidden="true" />}
            onClick={onTag}
          >
            Tag for job posting
          </Button>
        </PermissionGate>
      }
    />
  );
}

/** A count card's way to the rows it counts — the Applicants tab, on the applicants' own read key. */
function openApplicantsItem(stage: 'screening' | 'interview', onClick: () => void) {
  return {
    key: `open-applicants-${stage}`,
    label: 'Open applicants',
    icon: <Users className="h-4 w-4" aria-hidden="true" />,
    permission: 'applicants.read',
    onClick,
  };
}

/** Stage 1 — the posting's three values, as the product owner named them. */
function postingRows(cycle: VacancyCycle | null): PreviewRow[] {
  return [
    { label: 'Posted by', value: cycle?.postedByName ?? null },
    { label: 'Posted at', value: cycle?.postedDate != null ? formatDateOnly(cycle.postedDate) : null, mono: true },
    { label: 'Remarks', value: cycle?.remarks ?? null },
  ];
}

/**
 * Stage 2 — who published the posting, and when. Both STAMPED; a posting published before the step
 * existed carries neither, and reads em dashes rather than an invented stamp.
 */
function publishedRows(cycle: VacancyCycle | null): PreviewRow[] {
  return [
    { label: 'Published by', value: cycle?.isPublished ? (cycle.publishedByName ?? null) : null },
    {
      label: 'Published at',
      value: cycle?.isPublished && cycle.publishedAt != null ? formatDateTime(cycle.publishedAt) : null,
      mono: true,
    },
  ];
}

/** A count, muted at zero — a zero is a fact, not an absence. */
function count(value: number | undefined) {
  if (value === undefined) return null;
  return <span className={`font-mono tabular-nums ${value === 0 ? 'text-slate-300' : ''}`}>{value}</span>;
}

/** Stage 2 — how the posting's live applicants stand at screening. The outcomes partition the screened. */
function screeningRows(cycle: VacancyCycle | null): PreviewRow[] {
  return [
    { label: 'Applicants', value: count(cycle?.applicantCount) },
    { label: 'Screened', value: count(cycle?.screenedCount) },
    { label: 'Qualified', value: count(cycle?.qualifiedCount) },
    { label: 'Disqualified', value: count(cycle?.disqualifiedCount) },
    { label: 'Not screened yet', value: count(cycle ? cycle.applicantCount - cycle.screenedCount : undefined) },
  ];
}

/** Stage 3 — of the qualified, how many have an interview scheduled. */
function interviewRows(cycle: VacancyCycle | null): PreviewRow[] {
  return [
    { label: 'Scheduled', value: count(cycle?.interviewScheduledCount) },
    {
      label: 'Qualified, not yet scheduled',
      value: count(cycle ? cycle.qualifiedCount - cycle.interviewScheduledCount : undefined),
    },
  ];
}

/** What the header does not already say: the item number is the title; post code, post and unit the meta. */
function postRows(vacancy: VacancyListItem): PreviewRow[] {
  return [
    { label: 'Salary grade', value: vacancy.salaryGradeLabel },
    {
      label: 'Monthly salary',
      value: vacancy.monthlyAmount != null ? formatMoney(vacancy.monthlyAmount) : null,
      mono: true,
    },
    // The designation's qualification standards — what a posting advertises. Live from the
    // catalog, plain text; edited on the position, not here.
    { label: 'Education', value: vacancy.education },
    { label: 'Training', value: vacancy.training },
    { label: 'Experience', value: vacancy.experience },
    { label: 'Eligibility', value: vacancy.eligibilityName },
    { label: 'Other expectation', value: vacancy.otherExpectations },
  ];
}

/** The update half of the ITEM's trail — created-by is in the header for every tab. */
function auditRows(vacancy: VacancyListItem): PreviewRow[] {
  return [
    { label: 'Updated by', value: vacancy.updatedByName },
    {
      label: 'Updated',
      value: vacancy.updatedAt != null ? formatDateTime(vacancy.updatedAt) : null,
      mono: true,
    },
  ];
}
